import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ProtectedRoute } from '@/core/auth/ProtectedRoute';
import AppLayout from '@/layout/AppLayout';
import LoadingSpinner from '@/shared/components/LoadingSpinner/LoadingSpinner';
import LandingRedirect from '@/shared/components/LandingRedirect';

// Lazy load feature components for code splitting
const UserDashboard = lazy(() => import('@/features/dashboard/components/UserDashboard'));
const ProjectList = lazy(() => import('@/features/projects/components/ProjectList'));
const ProjectLayout = lazy(() => import('@/features/projects/components/ProjectLayout'));
const ProjectOverview = lazy(() => import('@/features/projects/components/ProjectOverview'));
const ProjectEnvironments = lazy(() => import('@/features/projects/components/ProjectEnvironments'));
const EnvironmentList = lazy(() => import('@/features/environments/components/EnvironmentList'));
const EnvironmentDetail = lazy(() => import('@/features/environments/components/EnvironmentDetail'));
const ConnectionList = lazy(() => import('@/features/connections/components/ConnectionList'));
const ContainerAppList = lazy(() => import('@/features/apps/components/ContainerAppList'));
const ContainerAppDetail = lazy(() => import('@/features/apps/components/ContainerAppDetail'));
const RecentChanges = lazy(() => import('@/features/history/components/RecentChanges'));
const ImageRepositories = lazy(() => import('@/features/images/components/ImageRepositories'));
const SyncStatusView = lazy(() => import('@/features/sync/components/SyncStatusView'));
const ReleaseListPage = lazy(() => import('@/features/releases/components/ReleaseListPage'));
const ReleaseForm = lazy(() => import('@/features/releases/components/ReleaseForm'));
const ReleaseDetailPage = lazy(() => import('@/features/releases/components/ReleaseDetailPage'));
const ProjectAnalytics = lazy(() => import('@/features/analytics/components/ProjectAnalytics'));

function withSuspense(Component: React.LazyExoticComponent<() => JSX.Element | null>) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <LandingRedirect />,
      },
      {
        path: 'dashboard',
        element: withSuspense(UserDashboard),
      },
      {
        path: 'projects',
        element: withSuspense(ProjectList),
      },
      {
        path: 'projects/:projectId',
        element: withSuspense(ProjectLayout),
        children: [
          {
            index: true,
            element: withSuspense(ProjectOverview),
          },
          {
            path: 'environments',
            element: withSuspense(ProjectEnvironments),
          },
          {
            path: 'environments/:environmentId',
            element: withSuspense(EnvironmentDetail),
          },
          {
            path: 'releases',
            element: withSuspense(ReleaseListPage),
          },
          {
            path: 'releases/new',
            element: withSuspense(ReleaseForm),
          },
          {
            path: 'releases/:releaseId',
            element: withSuspense(ReleaseDetailPage),
          },
          {
            path: 'releases/:releaseId/edit',
            element: withSuspense(ReleaseForm),
          },
          {
            path: 'analytics',
            element: withSuspense(ProjectAnalytics),
          },
        ],
      },
      {
        path: 'environments',
        element: withSuspense(EnvironmentList),
      },
      {
        path: 'environments/:environmentId',
        element: withSuspense(EnvironmentDetail),
      },
      {
        path: 'connections',
        element: withSuspense(ConnectionList),
      },
      {
        path: 'apps',
        element: withSuspense(ContainerAppList),
      },
      {
        path: 'apps/:appId',
        element: withSuspense(ContainerAppDetail),
      },
      {
        path: 'history',
        element: withSuspense(RecentChanges),
      },
      {
        path: 'images',
        element: withSuspense(ImageRepositories),
      },
      {
        path: 'sync',
        element: withSuspense(SyncStatusView),
      },
    ],
  },
  {
    path: '*',
    element: (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-2">404</h1>
          <p className="text-muted-foreground">Page not found</p>
        </div>
      </div>
    ),
  },
]);
